import { autoinject, transient, computedFrom, bindable, DOM } from "aurelia-framework";
import { GuidHelper } from "../../helpers/guid.helper";
import { Reflection } from "../../helpers/reflection";
import * as DOMPurify from "dompurify";
import Quill from "quill";
window["Quill"] = Quill;
const ImageResize = require('./scripts/image-resize.min.js');

//Load styles
require("./comp-rich-text-editor.scss");

//INFO - Do not update Quill js to version 2.0.2 as it has issues with the bullet points. they changed the <ul> & <ol> tags to always use <ul>
//and if we try to load old html content with <ol> tags, it will not work. So, we are sticking to version 1.3.7

@autoinject
@transient()
export class CompRichTextEditor {

    @bindable
    public toolbarPositionBottom: boolean;

    private _editor: HTMLDivElement;
    private _quill: any;
    private _observer: MutationObserver;
    public editor: HTMLDivElement;
    public context: any;
    public identifier: string = GuidHelper.generateGuid();
    public focused: boolean;
    private _modules: any = {
        imageResize: {
            modules: ['Resize', 'DisplaySize', 'Toolbar']
        },
        toolbar: [
            [{ header: [1, 2, false] }],
            ['bold', 'italic', 'underline'],
            ['image', 'code-block'],       // dropdown with defaults from theme
            [{ 'align': [] }],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{'color': []} ],
            [{'background': [] }]
        ],
        keyboard: {
            bindings: {
                indent: {
                    key: 'Tab',
                    handler() {
                        return false;
                    },
                },
                outdent: {
                    key: 'Tab',
                    shiftKey: true,
                    handler() {
                        return false;
                    },
                },
            }
        }
    };

    @bindable
    public placeholder: string;

    @bindable
    public html: string;

    @bindable
    public label: string;

    @bindable
    public info: string;

    @bindable
    public infoBottom: string;

    @bindable
    public allowImage: boolean = true;

    @bindable
    public allowLink: boolean = true;

    @bindable
    public allowVideo: boolean = true;

    @bindable
    public allowCode: boolean = true;

    @bindable
    public disabled: boolean = false;

    //Used in version 2.0.2 for setting the bounds of the editor for the tooltips
    // @bindable
    // public bounds: string;

    constructor(private element: Element) { }

    attached() {

        //If quill editor is already initialized, return
        if (this._quill) {
            return;
        }

        //Init quill editor
        this.init(this.editor);

        //Add focus and focusout events
        this.editor.addEventListener('focusin', () => {
            this.focused = true;
        }, false)

        this.editor.addEventListener('focusout', () => {
            this.focused = false;
        }, false)
    }

    detached() {

        //Remove focus and focusout events
        this.editor.removeEventListener('focusin', () => {
            this.focused = true;
        }, false);
        this.editor.removeEventListener('focusout', () => {
            this.focused = false;
        }, false);

        //Clean up the Quill editor instance
        if (this._quill) {
            this._quill.off('selection-change', this.onTextSelected);
            this._observer.disconnect();
            this.html = null;
            this._quill = null;
            this.element.getElementsByClassName('ql-toolbar').item(0).remove();
        }
    }

    public init(value: HTMLDivElement) {
        if (value) {

            //Import resize module
            if (!Quill["imports"]['modules/imageResize']) {
                Quill.register('modules/imageResize', ImageResize);
            }

            if (this.allowLink)
            {
                const linkModule = ['link'];
                const toolbar = this._modules.toolbar;
            
                // Check if the toolbar already contains ['link']
                const hasLinkModule = toolbar.some(item => JSON.stringify(item) === JSON.stringify(linkModule));
            
                if (!hasLinkModule) {
                    toolbar.push(linkModule);
                }
            }

            const quillConfig: any = {
                modules: this._modules,
                placeholder: this.placeholder,
                theme: 'snow',  // or 'bubble'
                // bounds: this.bounds -- for version 2.0.2
            };

            //Create quill editor instance
            this._quill = new Quill(value, quillConfig);

            //Spellcheck
            this._quill.root.spellcheck = true;
            this._quill.on('selection-change', this.onTextSelected);
            if (this.html) {
                this._quill.root.innerHTML = DOMPurify.sanitize(this.html);
            }
            const targetNode = this._quill.root;
            const config = { attributes: true, childList: true, subtree: true, characterData: true };

            const callback = () => {
                this.html = CompRichTextEditor.getOptimizedHtml(this._quill.root);
            };

            if (this._observer) {
                this._observer.disconnect();
            }

            this._observer = new MutationObserver(callback);
            this._observer.observe(targetNode, config);
        } 
        else {
            if (this._observer) {
                this._observer.disconnect();
            }
            this._quill = null;
            this.element.getElementsByClassName('ql-toolbar').item(0).remove();
        }
        this._editor = value;
        this.disabledChanged();
        this.dispatchEvent('editor-ready', {});
    }

    @computedFrom(Reflection.getPropertyPath<CompRichTextEditor>(z => z._quill))
    get quillInstance() {
        return this._quill;
    }

    public onTextSelected = (range, oldRange, source) => {
        const event: CustomEvent = DOM.createCustomEvent("text-selected", { detail: { range, oldRange, source }, bubbles: true });
        this.element.dispatchEvent(event);
    }

    public disabledChanged() {
        if (!this._quill) {
            return;
        }

        if (this.disabled) {
            this._quill.disable();
        }
        else {
            this._quill.enable();
        }
    }

    public htmlChanged(newVal) {
        if (this._editor && this._quill) {
            const currentHtml = CompRichTextEditor.getOptimizedHtml(this._quill.root);
            if (newVal != currentHtml) {
                this._quill.root.innerHTML = DOMPurify.sanitize(newVal);
                this.dispatchEvent('editor-ready', {});
            }
        }
    }

    public static getOptimizedHtml(html: HTMLElement | string) {

        let htmlElement: HTMLElement = null;

        if (typeof html === 'string') {
            const textToSearch = DOMPurify.sanitize(html);
            htmlElement = document.createElement('div');
            htmlElement.innerHTML = textToSearch;
        } else {
            htmlElement = html;
        }

        const node = htmlElement.cloneNode(true) as HTMLElement;

        const variableSpans = node.querySelectorAll('.variable-placeholder');

        for (let index = 0; index < variableSpans.length; index++) {
            const span = variableSpans.item(index);
            const text = span.textContent;
            span.children[0].innerHTML = text.trim();
        }

        const images = node.getElementsByTagName('img') as HTMLCollection;

        const qlCursors = node.getElementsByClassName("ql-cursor");
        for (let index = 0; index < qlCursors.length; index++) {
            const element = qlCursors.item(index);
            element.remove();
        } // fix for ? being added. this was being added after newline with toolbar controls like bold and italic

        //Set target to all anchor tags as blank 
        node.querySelectorAll('a').forEach((link) => {
            link.setAttribute('target', '_blank');
        })

        return node.textContent != null || images.length > 0 ? node.innerHTML : null;
    }

    private dispatchEvent(eventName: string, data: any): void {
        const paramsEvent: any = { detail: data, bubbles: true };
        const event: CustomEvent = new CustomEvent(eventName, paramsEvent);
        this.element.dispatchEvent(event);
    }
}
